// src/components/Auth/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { Lock, Key, User, AlertCircle, Fingerprint } from 'lucide-react';
import toast from 'react-hot-toast';
import { startAuthentication } from '@simplewebauthn/browser';

interface LoginPageProps {
  onLogin: (userId: string, username: string, masterPassword: string, encryptedDataKey: string, salt: string) => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [isFingerprintSupported, setIsFingerprintSupported] = useState(false);
  const [isFingerprintLogin, setIsFingerprintLogin] = useState(false);

  // ✅ 2. 新增：裝置金鑰狀態（放在組件頂層）
  const [hasDeviceKey, setHasDeviceKey] = useState(false);

  // ✅ 3. useEffect 放在組件頂層
  useEffect(() => {
    const savedUsername = localStorage.getItem('last_username');
    if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []);

  useEffect(() => {
    const checkSupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setIsFingerprintSupported(available);
        } catch {
          setIsFingerprintSupported(false);
        }
      }
    };
    checkSupport();
  }, []);

  // ✅ 新增：檢查 device_key 是否存在（新增這個！）
  useEffect(() => {
    // 檢查是否有任何 device_key_* 存在
    const hasKey = Object.keys(localStorage).some(key => key.startsWith('device_key_'));
    setHasDeviceKey(hasKey);
  }, []);  // ← 空陣列，只在組件載入時執行一次

	// ✅ 指紋登錄
	const handleFingerprintLogin = async () => {
		if (!username.trim()) {
			toast.error('請先輸入使用者名稱');
			return;
		}

		setIsFingerprintLogin(true);
		setError('');

		try {
			// 1. 獲取登錄選項（同時取得 userId）
			const optionsRes = await fetch(`/api/auth/webauthn-login?username=${encodeURIComponent(username.trim())}`);
			if (!optionsRes.ok) {
				const err = await optionsRes.json();
				if (optionsRes.status === 404) {
					toast.error('此帳號尚未註冊指紋，請前往設定註冊');
					return;
				}
				throw new Error(err.error || '獲取登錄選項失敗');
			}

			const options = await optionsRes.json();

			// ✅ 從 options 中獲取 userId（用於讀取 device_key）
			const userId = options.userId;
			if (!userId) {
				throw new Error('無法獲取使用者 ID');
			}

			// ✅ 用 userId 作為 key 讀取 device_key
			const storageKey = `device_key_${userId}`;
			const deviceKey = localStorage.getItem(storageKey);
			
			if (!deviceKey) {
				toast.error('請先使用密碼登入一次以建立裝置金鑰');
				setIsFingerprintLogin(false);
				return;
			}

			// 2. 調用系統指紋驗證
			const attestation = await startAuthentication({ optionsJSON: options });

			// 3. 驗證指紋
			const verifyRes = await fetch('/api/auth/webauthn-login', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					userId: options.userId,
					attestation
				})
			});

			const data = await verifyRes.json();
			if (!verifyRes.ok) {
				throw new Error(data.error || '指紋驗證失敗');
			}

			toast.success('指紋驗證成功！');

			// ✅ 指紋登錄成功，使用 Device Key
			onLogin(
				data.user.id,
				data.user.username,
				deviceKey,
				data.user.encryptedDataKey || '',
				data.user.salt || ''
			);

		} catch (err) {
			const msg = err instanceof Error ? err.message : '指紋登錄失敗';
			setError(msg);
			toast.error(msg);
		} finally {
			setIsFingerprintLogin(false);
		}
	};

  // ✅ 密碼登入 / 註冊
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('請輸入使用者名稱');
      return;
    }
    if (username.length < 3) {
      setError('使用者名稱至少 3 個字元');
      return;
    }
    if (!password) {
      setError('請輸入密碼');
      return;
    }

    if (isRegister) {
      if (password.length < 6) {
        setError('密碼至少 6 個字元');
        return;
      }
      if (password !== confirmPassword) {
        setError('兩次輸入的密碼不一致');
        return;
      }
    }

    setIsLoading(true);

    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '操作失敗');
      }

      toast.success(isRegister ? '註冊成功！' : '歡迎回來！');

      localStorage.setItem('last_username', username.trim());
      localStorage.setItem('user_id', data.user.id);
      localStorage.setItem('username', data.user.username);

			// 儲存 Device Key 到 localStorage（綁定 userId）
			if (data.user.deviceKey) {
				const storageKey = `device_key_${data.user.id}`;
				localStorage.setItem(storageKey, data.user.deviceKey);
			}

      onLogin(
        data.user.id,
        data.user.username,
        password,
        data.user.encryptedDataKey || '',
        data.user.salt || ''
      );

    } catch (err) {
      const msg = err instanceof Error ? err.message : '操作失敗';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4">
            <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">NOTEBOOK++</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            {isRegister ? '建立新帳號' : '登入您的帳號'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              使用者名稱
              {localStorage.getItem('last_username') && (
                <span className="text-xs text-gray-400 ml-2">（上次使用：{localStorage.getItem('last_username')}）</span>
              )}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="請輸入使用者名稱（至少 3 字）"
                disabled={isLoading || isFingerprintLogin}
                autoFocus
              />
            </div>
          </div>

          {!isFingerprintLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isRegister ? '設定密碼' : '密碼'}
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                  placeholder={isRegister ? '請設定密碼（至少 6 位）' : '請輸入密碼'}
                  disabled={isLoading}
                  minLength={6}
                />
              </div>
              {isRegister && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  ⚠️ 此密碼同時用於加密筆記，忘記後無法復原資料！
                </p>
              )}
            </div>
          )}

          {isRegister && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                確認密碼
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                  placeholder="再次輸入密碼"
                  disabled={isLoading}
                  minLength={6}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                {isRegister ? '註冊中...' : '登入中...'}
              </>
            ) : (
              isRegister ? '註冊' : '登入'
            )}
          </button>
        </form>

        {/* 指紋登錄按鈕 */}
        {!isRegister && isFingerprintSupported && (
          <div className="mt-3">
            <button
              onClick={handleFingerprintLogin}
              disabled={isFingerprintLogin || isLoading}
              className="w-full border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Fingerprint className="w-5 h-5" />
              {isFingerprintLogin ? (
                <>
                  <span className="animate-spin">⏳</span>
                  驗證指紋中...
                </>
              ) : (
                '🔐 使用指紋登錄'
              )}
            </button>
						{/*✅ 在 UI 中使用 */}
						{hasDeviceKey ? (
							<p className="text-xs text-green-500 text-center mt-2">✅ 裝置金鑰已就緒</p>
						) : (
							<p className="text-xs text-yellow-500 text-center mt-2">⚠️ 請先使用密碼登入一次以建立裝置金鑰</p>
						)}
          </div>
        )}

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsRegister(!isRegister);
              setError('');
              setPassword('');
              setConfirmPassword('');
            }}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {isRegister ? '已有帳號？點此登入' : '還沒有帳號？點此註冊'}
          </button>
        </div>
      </div>
    </div>
  );
};